import { FormattedMessage, useIntl } from "react-intl";
import { useAuthContext } from "../../../../context/AuthContext";
import { useManageUsersContext } from "../../../../context/ManageUsersContext";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../../hooks/useAuthorizedAction";
import Role from "../../../../models/role";
import { UserDetailsFormInputs } from "../../../../models/users.interface";
import authorizationService from "../../../../services/authorization.service";
import ConnectForm from "../../../Shared/ConnectForm/ConnectForm";
import ToggleSwitchFormInput from "../../../Shared/ToggleSwitch/ToggleSwitchFormInput";
import styles from "./UserPermissionInputs.module.css";
import { getUserFromToken } from "../../../../utils/authorizationUtil";
import LocalStorageUtil from "../../../../utils/localStorage";
import { Constants } from "../../../../constants";

type UserPermissionInputsProps = {
  disabled?: boolean;
};

const UserPermissionInputs = ({ disabled }: UserPermissionInputsProps) => {
  const intl = useIntl();
  const {
    state: { user },
  } = useAuthContext();
  const {
    state: {
      userOrgPermission: { isAllowed, loading },
    },
  } = useManageUsersContext();

  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );

  const isAdmin =
    user && authorizationService.hasRole(user.role, [Role.OrgAdmin]);

  const isAdminPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.SET_ADMIN"
  ] as string;
  const loginPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_PORTAL_LOGIN"
  ] as string;

  const viewEcgPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_VIEW_ECG"
  ] as string;

  const viewAllEcgsInEntityPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_VIEW_ALL_ECGS_IN_ENTITY"
  ] as string;

  const interpretPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_EDIT_INTERPRETATION"
  ] as string;

  const emailEcgsPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_EMAIL_ECGS"
  ] as string;

  const managePatientsPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_MANAGE_PATIENTS"
  ] as string;

  const downloadReportsPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_DOWNLOAD_REPORTS"
  ] as string;

  const principalInvestigatorPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.PRINCIPAL_INVESTIGATOR"
  ] as string;

  const archiveEcgsPlaceholder = intl.messages[
    "MANAGEMENT.USER_DETAILS.CAN_ARCHIVE_ECGS"
  ] as string;

  const allowViewSelfClinicsEcgs =
    getUserFromToken(LocalStorageUtil.get(Constants.JWT_KEY))?.Organization
      .IsViewSelfClinicIsAllowed || false;

  return (
    <ConnectForm<UserDetailsFormInputs>>
      {({ control, watch, formState: { isSubmitting } }) => {
        const isAdminSwitchOn = watch("isAdmin", false); //"canManageProUsers", false);

        return (
          <div className="p-2">
            <p className={styles.title}>
              <FormattedMessage
                id="USER.PERMISSIONS"
                defaultMessage="Permissions"
              />
              :
            </p>
            <ul className="pl-3 m-0 list-unstyled">
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canManageProUsers"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: isAdminPlaceholder,
                    disabled: isSubmitting || !isAdmin || loading || disabled,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canLogIntoPortal"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: loginPlaceholder,
                    disabled:
                      isSubmitting ||
                      isAdminSwitchOn ||
                      loading ||
                      disabled ||
                      !isAllowed?.canLogIntoPortal,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canViewEcgs"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: viewEcgPlaceholder,
                    disabled:
                      isSubmitting ||
                      loading ||
                      disabled ||
                      !isAllowed?.canViewEcgs,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canViewSelfClinicsEcgs"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: viewAllEcgsInEntityPlaceholder,
                    disabled:
                      isSubmitting ||
                      loading ||
                      disabled ||
                      !isAllowed?.canViewSelfClinicsEcgs ||
                      !allowViewSelfClinicsEcgs,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canEditInterpretation"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: interpretPlaceholder,
                    disabled:
                      isSubmitting ||
                      isAdminSwitchOn ||
                      loading ||
                      disabled ||
                      !isAllowed.canEditInterpretation,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canArchiveEcgs"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: archiveEcgsPlaceholder,
                    disabled:
                      isSubmitting ||
                      loading ||
                      disabled ||
                      !isAllowed.canArchiveEcgs,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canSendEcgsViaMail"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: emailEcgsPlaceholder,
                    disabled:
                      isSubmitting ||
                      isAdminSwitchOn ||
                      loading ||
                      disabled ||
                      !isAllowed.canSendEcgsViaMail,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canManageNonProUsers"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: managePatientsPlaceholder,
                    disabled:
                      isSubmitting ||
                      disabled ||
                      !isAllowed.canManageNonProUsers,
                  }}
                />
              </li>
              <li className="mb-2">
                <ToggleSwitchFormInput<UserDetailsFormInputs>
                  name={"canViewReports"}
                  control={control}
                  input={{
                    offLabel: "",
                    onLabel: downloadReportsPlaceholder,
                    disabled:
                      isSubmitting ||
                      loading ||
                      disabled ||
                      !isAllowed.canViewReports,
                  }}
                />
              </li>
              {(isCroOrg || isCroExternOrg) && (
                <li>
                  <ToggleSwitchFormInput
                    name={"canApproveEcg"}
                    control={control}
                    input={{
                      offLabel: "",
                      onLabel: principalInvestigatorPlaceholder,
                      disabled:
                        isSubmitting ||
                        loading ||
                        disabled ||
                        !isAllowed.canApproveEcg,
                    }}
                  />
                </li>
              )}
            </ul>
          </div>
        );
      }}
    </ConnectForm>
  );
};

export default UserPermissionInputs;
